import { formatClubDate } from './clubTime';
export interface ApprovedCatch {
  id: string;
  eventId: string;
  species: string;
  lengthCm: number;
  competitorName: string;
  keptOrReleased: string | null;
  submissionTimestamp: string | null;
}

export interface CompetitionEvent {
  id: string;
  title: string;
  eventDate: string;
}

export interface SeasonSummary {
  totalCatches: number;
  keptCount: number;
  releasedCount: number;
  keptPercent: number;
  releasedPercent: number;
  uniqueCompetitors: number;
  competitionsHeld: number;
}

export interface SpeciesStats {
  species: string;
  count: number;
  avgLength: number;
  maxLength: number;
  maxLengthHolder: string;
  keptCount: number;
  releasedCount: number;
}

export interface CompetitorRanking {
  name: string;
  totalCatches: number;
  biggestFish: number;
  competitionsParticipated: number;
}

export interface BiggestFishRecord {
  species: string;
  lengthCm: number;
  competitorName: string;
  eventId: string;
}

export interface CompetitionTrend {
  eventId: string;
  eventTitle: string;
  eventDate: string;
  totalCatches: number;
  avgLength: number;
  uniqueCompetitors: number;
}

export interface PersonalSpeciesBest {
  species: string;
  lengthCm: number;
}

export function filterCatchesByYear(catches: ApprovedCatch[], events: CompetitionEvent[], year: number): ApprovedCatch[] {
  const yearEventIds = new Set(events.filter((e) => new Date(e.eventDate).getFullYear() === year).map((e) => e.id));
  return catches.filter((c) => yearEventIds.has(c.eventId));
}

export function computeSeasonSummary(catches: ApprovedCatch[], events: CompetitionEvent[]): SeasonSummary {
  const total = catches.length;
  const withStatus = catches.filter((c) => c.keptOrReleased != null);
  const kept = withStatus.filter((c) => c.keptOrReleased === 'KEPT').length;
  const released = withStatus.filter((c) => c.keptOrReleased === 'RELEASED').length;
  const statusTotal = withStatus.length;
  const eventIdsWithCatches = new Set(catches.map((c) => c.eventId));

  return {
    totalCatches: total,
    keptCount: kept,
    releasedCount: released,
    keptPercent: statusTotal > 0 ? Math.round((kept / statusTotal) * 100) : 0,
    releasedPercent: statusTotal > 0 ? Math.round((released / statusTotal) * 100) : 0,
    uniqueCompetitors: new Set(catches.map((c) => c.competitorName)).size,
    competitionsHeld: events.filter((e) => eventIdsWithCatches.has(e.id)).length,
  };
}

export function computeSpeciesBreakdown(catches: ApprovedCatch[]): SpeciesStats[] {
  const bySpecies: Record<string, ApprovedCatch[]> = {};
  for (const c of catches) {
    (bySpecies[c.species] ||= []).push(c);
  }
  return Object.entries(bySpecies).map(([species, list]) => {
    const sorted = list.sort((a, b) => b.lengthCm - a.lengthCm);
    const withStatus = list.filter((c) => c.keptOrReleased != null);
    return {
      species,
      count: list.length,
      avgLength: Math.round((list.reduce((s, c) => s + c.lengthCm, 0) / list.length) * 10) / 10,
      maxLength: sorted[0].lengthCm,
      maxLengthHolder: sorted[0].competitorName,
      keptCount: withStatus.filter((c) => c.keptOrReleased === 'KEPT').length,
      releasedCount: withStatus.filter((c) => c.keptOrReleased === 'RELEASED').length,
    };
  }).sort((a, b) => b.count - a.count);
}

export function computeCompetitorLeaderboard(catches: ApprovedCatch[]): CompetitorRanking[] {
  const byName: Record<string, ApprovedCatch[]> = {};
  for (const c of catches) {
    (byName[c.competitorName] ||= []).push(c);
  }
  return Object.entries(byName).map(([name, list]) => ({
    name,
    totalCatches: list.length,
    biggestFish: Math.max(...list.map((c) => c.lengthCm)),
    competitionsParticipated: new Set(list.map((c) => c.eventId)).size,
  })).sort((a, b) => b.totalCatches - a.totalCatches);
}

export function findBiggestFish(catches: ApprovedCatch[]): BiggestFishRecord | null {
  if (catches.length === 0) return null;
  const biggest = catches.reduce((max, c) => c.lengthCm > max.lengthCm ? c : max, catches[0]);
  return { species: biggest.species, lengthCm: biggest.lengthCm, competitorName: biggest.competitorName, eventId: biggest.eventId };
}

export function computeCompetitionTrends(catches: ApprovedCatch[], events: CompetitionEvent[]): CompetitionTrend[] {
  const eventMap = new Map(events.map((e) => [e.id, e]));
  const byEvent: Record<string, ApprovedCatch[]> = {};
  for (const c of catches) {
    (byEvent[c.eventId] ||= []).push(c);
  }
  return Object.entries(byEvent)
    .map(([eventId, list]) => {
      const evt = eventMap.get(eventId);
      return {
        eventId,
        eventTitle: evt?.title || eventId,
        eventDate: evt?.eventDate || '',
        totalCatches: list.length,
        avgLength: Math.round((list.reduce((s, c) => s + c.lengthCm, 0) / list.length) * 10) / 10,
        uniqueCompetitors: new Set(list.map((c) => c.competitorName)).size,
      };
    })
    .sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime());
}

export function computePersonalSpeciesBests(catches: ApprovedCatch[]): PersonalSpeciesBest[] {
  const bySpecies: Record<string, number> = {};
  for (const c of catches) {
    bySpecies[c.species] = Math.max(bySpecies[c.species] || 0, c.lengthCm);
  }
  return Object.entries(bySpecies).map(([species, lengthCm]) => ({ species, lengthCm })).sort((a, b) => b.lengthCm - a.lengthCm);
}

function escapeCSV(val: any): string {
  if (val == null) return '';
  const s = String(val);
  // Neutralise formula injection: prefix with tab if value starts with a formula trigger character
  const formulaTriggers = ['=', '+', '-', '@', '\t', '\r'];
  const needsQuoting = s.includes(',') || s.includes('"') || s.includes('\n');
  const needsFormulaPrefix = formulaTriggers.some((c) => s.startsWith(c));
  if (needsFormulaPrefix) {
    // Prepend a tab to neutralise formula interpretation in Excel/Sheets/LibreOffice
    const safe = `\t${s}`;
    return `"${safe.replace(/"/g, '""')}"`;
  }
  if (needsQuoting) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function fmtDateAU(d: string | null): string {
  if (!d) return '';
  try {
    return formatClubDate(d);
  } catch { return ''; }
}

export function generateCsvContent(catches: ApprovedCatch[], events: CompetitionEvent[]): string {
  const eventMap = new Map(events.map((e) => [e.id, e]));
  const headers = ['Date', 'Competition', 'Competitor', 'Species', 'Length (cm)', 'Kept/Released', 'Event ID'];
  const rows = catches.map((c) => {
    const evt = eventMap.get(c.eventId);
    return [
      escapeCSV(fmtDateAU(c.submissionTimestamp)),
      escapeCSV(evt?.title || ''),
      escapeCSV(c.competitorName),
      escapeCSV(c.species),
      escapeCSV(c.lengthCm),
      escapeCSV(c.keptOrReleased || ''),
      escapeCSV(c.eventId),
    ].join(',');
  });
  return [headers.join(','), ...rows].join('\n');
}
